import {AuthenticatedUser, RegistrationRequest, User, UserAccount, UserAccountProvider} from 'services/entities/auth';
import {callAPI} from 'services/api';
import {AuthenticationError, NotAuthorizedError, ValueError} from 'util/errors';
import {getSessionItem, removeSessionItem, setSessionItem, startNewSession} from 'util/session';
import {getCacheItem, removeCacheItem, setCacheItem} from 'util/cache';
import {TimeSpan} from 'util/time';

const AUTH_TOKEN_KEY = 'api.auth.token';
const AUTH_USER_KEY = 'api.auth.user';

export async function login(email: string, password: string): Promise<string> {
  try {
    const responseJSON = await callAPI('/auth/token/', 'PUT', { email: email, password: password });
    const token = responseJSON.token;
    setAuthToken(token);
    return responseJSON.token;
  } catch(err) {
    if(err instanceof ValueError && err.errors.length > 0) {
      throw new AuthenticationError(err.errors[0]);
    } else {
      throw err;
    }
  }
}

export function logout() {
  removeCacheItem(AUTH_USER_KEY);
  startNewSession();
}

export function readUser(userJSON: any): User {
  return {
    username: userJSON.username,
    firstName: userJSON.first_name,
    lastName: userJSON.last_name,
    email: userJSON.email
  };
}

export function readAuthenticatedUser(userJSON: any): AuthenticatedUser {
  const accounts: UserAccount[] = userJSON.accounts.map((acc: any) => {
    const providerTypeStr: keyof typeof UserAccountProvider = (acc as any).type;
    return { avatarURL: acc.avatar, provider: UserAccountProvider[providerTypeStr] };
  });
  return {
    username: userJSON.username,
    firstName: userJSON.first_name,
    lastName: userJSON.last_name,
    email: userJSON.email,
    groups: userJSON.groups,
    accounts: accounts
  };
}

export async function loadUser(ignoreCache: boolean = false): Promise<AuthenticatedUser> {
  let user = getCacheItem(AUTH_USER_KEY);
  if(user && !ignoreCache) {
    return user;
  }
  let responseUser;
  try {
    responseUser = await callAPI('/auth/user/', 'GET', null, true);
  } catch(err) {
    if(err instanceof NotAuthorizedError) {
      removeSessionItem(AUTH_TOKEN_KEY);
      removeCacheItem(AUTH_USER_KEY);
    }
    throw err;
  }
  if(!responseUser) {
    throw new Error("User could not be loaded");
  }
  user = readAuthenticatedUser(responseUser);
  setCacheItem(AUTH_USER_KEY, user, new TimeSpan({days: 1}));
  return user;
}

export async function register(request: RegistrationRequest): Promise<string> {
  const formattedRequest = {
    name: request.name,
    email: request.email,
    password: request.password,
    marketing_consent: request.marketingConsent
  };
  const responseJSON = await callAPI('/auth/register/', 'POST', formattedRequest);
  const token = responseJSON.token;
  setAuthToken(token);
  return responseJSON.token;
}

export async function requestPasswordReset(email: string): Promise<any> {
  await callAPI('/auth/request-password-reset/', 'PUT', { email: email });
}

export async function resetPassword(password: string, token: string): Promise<any> {
  await callAPI('/auth/reset-password/', 'PUT', { password: password, token: token });
}

export function isLoggedIn(): boolean {
  return !!getAuthToken();
}

export function getAuthToken(): string|null {
  return getSessionItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string) {
  setSessionItem(AUTH_TOKEN_KEY, token);
  removeCacheItem(AUTH_USER_KEY);
}